/**
 * Settings section for the einbuergerungstest app: Bundesland + simulation
 * preset. Stored as JSON in `app_settings.config` for this app only.
 *
 * Bundesland switch DOES NOT wipe prior progress on state-specific cards
 * (locked decision — see blog/ideas/product-decisions.md § "State-specific
 * content: preserve, don't wipe, on switch"). The select just changes the
 * server-side filter applied to practice / simulation pools.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  BUNDESLAND_CODES,
  BUNDESLAND_NAMES,
  type BundeslandCode,
  type EinbuergerungsConfig,
  type SimulationPreset,
} from '@/core/types';
import { api } from '@/lib/api';
import { useAppConfig } from '@/lib/queries';

const APP_ID = 'einbuergerungstest';

const PRESET_LABELS: Record<SimulationPreset, { name: string; rules: string }> = {
  bamf: { name: 'BAMF (official)', rules: '33 questions · pass at 17 correct · no timer' },
  hard: { name: 'Hard mode', rules: '30 questions · pass at 25 correct · no timer' },
};

const DEFAULTS: EinbuergerungsConfig = {
  bundesland: 'BW',
  simulation_preset: 'bamf',
};

export function EinbuergerungstestSection() {
  const queryClient = useQueryClient();
  const configQuery = useAppConfig<EinbuergerungsConfig>(APP_ID);

  const [bundesland, setBundesland] = useState<BundeslandCode>(DEFAULTS.bundesland);
  const [preset, setPreset] = useState<SimulationPreset>(DEFAULTS.simulation_preset);

  // Hydrate local form state once the config loads.
  useEffect(() => {
    const c = configQuery.data?.config;
    if (!c) return;
    if (c.bundesland) setBundesland(c.bundesland);
    if (c.simulation_preset) setPreset(c.simulation_preset);
  }, [configQuery.data]);

  const mutation = useMutation({
    mutationFn: (config: EinbuergerungsConfig) => api.saveAppConfig(APP_ID, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps', APP_ID, 'config'] });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      toast.success('Saved');
    },
    onError: (err: Error) => toast.error('Failed to save', { description: err.message }),
  });

  const current = configQuery.data?.config ?? DEFAULTS;
  const dirty =
    bundesland !== (current.bundesland ?? DEFAULTS.bundesland) ||
    preset !== (current.simulation_preset ?? DEFAULTS.simulation_preset);

  if (configQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Loading…</p>;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ bundesland, simulation_preset: preset });
      }}
      className="flex flex-col gap-6"
    >
      <Field
        label="Bundesland"
        hint="Determines which 10 state-specific questions appear in your practice and simulation pools. Changing this never deletes progress."
      >
        <NativeSelect
          value={bundesland}
          onChange={(e) => setBundesland(e.target.value as BundeslandCode)}
        >
          {BUNDESLAND_CODES.map((code) => (
            <option key={code} value={code}>
              {BUNDESLAND_NAMES[code]}
            </option>
          ))}
        </NativeSelect>
      </Field>

      <Field
        label="Simulation preset"
        hint="The official BAMF exam is 33 questions; the Hard preset trains tighter."
      >
        <div className="flex flex-col gap-2">
          {(['bamf', 'hard'] as const).map((value) => (
            <label
              key={value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
                preset === value ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <input
                type="radio"
                name="simulation_preset"
                value={value}
                checked={preset === value}
                onChange={() => setPreset(value)}
                className="mt-1"
              />
              <span className="flex flex-col">
                <span className="font-medium">{PRESET_LABELS[value].name}</span>
                <span className="text-muted-foreground text-xs">{PRESET_LABELS[value].rules}</span>
              </span>
            </label>
          ))}
        </div>
      </Field>

      <div>
        <Button type="submit" disabled={!dirty || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  );
}
